"use client";

export const ADMIN_ORG_SELECTION_STORAGE_KEY = "lynx-admin-org-selection";

export type AdminOrgSelection = {
	organizationId: string;
	organizationName?: string;
	timestamp: number;
};

export type AdminOrganizationInfo = {
	id: string;
	name: string;
	imageUrl: string | null;
};

type ResolveAdminFallbackTenantIdOptions = {
	tenantId?: string;
	storedSelectionOrgId?: string | null;
	activeTenantId?: string | null;
	databaseTenantIds: string[];
	membershipTenantIds: string[];
};

type ResolveAdminFallbackTenantIdResult = {
	fallbackTenantId: string | null;
	shouldStayOnTenant: boolean;
};

type ResolveAdminCurrentOrganizationOptions = {
	activeOrganization: AdminOrganizationInfo | null;
	availableOrganizations: AdminOrganizationInfo[];
	storedSelection: AdminOrgSelection | null;
	tenantId?: string;
};

function canUseSessionStorage() {
	return typeof window !== "undefined" && "sessionStorage" in window;
}

function isValidOrganizationId(
	organizationId: string | null | undefined,
): organizationId is string {
	return Boolean(
		organizationId &&
			organizationId !== "undefined" &&
			organizationId !== "null",
	);
}

function toSnapshotSelection(selection: AdminOrgSelection) {
	return JSON.stringify(selection);
}

export function readAdminOrgSelection(): AdminOrgSelection | null {
	if (!canUseSessionStorage()) {
		return null;
	}

	try {
		const value = window.sessionStorage.getItem(
			ADMIN_ORG_SELECTION_STORAGE_KEY,
		);
		if (!value) {
			return null;
		}

		const parsed = JSON.parse(value) as Partial<AdminOrgSelection>;
		if (!isValidOrganizationId(parsed.organizationId)) {
			return null;
		}

		return {
			organizationId: parsed.organizationId,
			organizationName:
				typeof parsed.organizationName === "string"
					? parsed.organizationName
					: undefined,
			timestamp: typeof parsed.timestamp === "number" ? parsed.timestamp : 0,
		};
	} catch {
		return null;
	}
}

export function writeAdminOrgSelection(selection: AdminOrgSelection) {
	if (!canUseSessionStorage()) {
		return;
	}

	window.sessionStorage.setItem(
		ADMIN_ORG_SELECTION_STORAGE_KEY,
		toSnapshotSelection(selection),
	);
}

export function clearAdminOrgSelection() {
	if (!canUseSessionStorage()) {
		return;
	}

	window.sessionStorage.removeItem(ADMIN_ORG_SELECTION_STORAGE_KEY);
}

export function createAdminOrgSelection(
	organizationId: string,
	organizationName?: string,
): AdminOrgSelection {
	return {
		organizationId,
		organizationName,
		timestamp: Date.now(),
	};
}

export function resolveAdminFallbackTenantId({
	tenantId,
	storedSelectionOrgId,
	activeTenantId,
	databaseTenantIds,
	membershipTenantIds,
}: ResolveAdminFallbackTenantIdOptions): ResolveAdminFallbackTenantIdResult {
	if (isValidOrganizationId(tenantId)) {
		return {
			shouldStayOnTenant: true,
			fallbackTenantId: null,
		};
	}

	const firstAvailableTenantId =
		databaseTenantIds[0] ?? membershipTenantIds[0] ?? null;
	const knownTenantIds = new Set([
		...databaseTenantIds,
		...membershipTenantIds,
		...(isValidOrganizationId(activeTenantId) ? [activeTenantId] : []),
	]);
	const fallbackTenantId =
		[storedSelectionOrgId, activeTenantId, firstAvailableTenantId].find(
			(candidateTenantId) =>
				isValidOrganizationId(candidateTenantId) &&
				knownTenantIds.has(candidateTenantId),
		) ?? null;

	return {
		shouldStayOnTenant: false,
		fallbackTenantId,
	};
}

export function resolveAdminCurrentOrganization({
	activeOrganization,
	availableOrganizations,
	storedSelection,
	tenantId,
}: ResolveAdminCurrentOrganizationOptions): AdminOrganizationInfo | null {
	if (isValidOrganizationId(tenantId)) {
		const urlOrganization = availableOrganizations.find(
			(organization) => organization.id === tenantId,
		);
		if (urlOrganization) {
			return urlOrganization;
		}

		if (storedSelection?.organizationId === tenantId) {
			return {
				id: tenantId,
				name: storedSelection.organizationName ?? tenantId,
				imageUrl: null,
			};
		}

		return null;
	}

	if (isValidOrganizationId(storedSelection?.organizationId)) {
		const storedOrganization = availableOrganizations.find(
			(organization) => organization.id === storedSelection.organizationId,
		);
		if (storedOrganization) {
			return storedOrganization;
		}

		return {
			id: storedSelection.organizationId,
			name: storedSelection.organizationName ?? storedSelection.organizationId,
			imageUrl: null,
		};
	}

	return activeOrganization ?? availableOrganizations[0] ?? null;
}
